import cn from 'classnames';
import SmallCaps from 'components/typography/small-caps';
import { LOCALES } from 'config/constants/locales';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { stripLocalePathPrefix } from 'utils/i18n/strip-locale-path-prefix';
import { getNextRouterLocale } from 'utils/i18n/next-locales';
import { useConfig } from 'config/use-config';

interface LocaleSwitcherProps {
  className?: string;
}

const availableLocales = [
  { code: LOCALES.DE, label: 'Deutsch' },
  { code: LOCALES.EN, label: 'English' },
  { code: LOCALES.NL, label: 'Nederlands' },
] as const;

/**
 * LocaleSwitcher component allows users to switch between visible language locales.
 */
const LocaleSwitcher: React.FC<LocaleSwitcherProps> = ({ className }) => {
  const { locale } = useIntl();
  const { locale: defaultLocale } = useConfig();
  const { asPath } = useRouter();
  const switchHref = stripLocalePathPrefix(asPath || '/');

  const handleLocaleClick = (event: MouseEvent<HTMLAnchorElement>, nextLocale: string): void => {
    if (nextLocale === locale) event.preventDefault();
  };

  return (
    <div className={className}>
      <SmallCaps>
        <div className="text-primary-main mb-2 text-center whitespace-nowrap lg:text-left">
          <FormattedMessage id="footer:link-section:language-switcher" />
        </div>
      </SmallCaps>
      <nav aria-label="Language Switcher" data-test="locale-switcher">
        <ul className="flex justify-center space-x-4 md:justify-start">
          {availableLocales.map(({ code, label }) => (
            <li key={code}>
              <Link
                href={switchHref}
                locale={getNextRouterLocale(code, defaultLocale)}
                scroll={false}
                className={cn(
                  'text-grey-700 lg:hover:text-grey-900 block text-sm lg:text-base',
                  locale === code ? 'underline underline-offset-4' : ''
                )}
                aria-current={locale === code ? 'page' : undefined}
                data-test={`locale-switcher-${code}`}
                onClick={(event) => handleLocaleClick(event, code)}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default LocaleSwitcher;
